import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles.css";
import { filterNavItems } from "../filterNavItems";
import type { PersonResult, UniversalSearchProps } from "../types";
import { HintGroup, Kbd } from "./Kbd";
import { ResultRow } from "./ResultRow";
import { SearchSvg } from "./icons";
import { SectionHeader } from "./SectionHeader";
import type { NavResult, PersonResultInternal, ResultItem } from "./types";
import { useDebounce } from "./useDebounce";

// Buckets nav results by their originating `NavItem.group` (carried through
// as `subtitle` — see the navResultItems mapping below), preserving the
// order each group first appears in, so the browsing view mirrors the
// consumer's own sidebar sections (e.g. Core / Products / Settings) instead
// of one flat list.
function groupByCategory(items: NavResult[]): { group: string; items: NavResult[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, NavResult[]>();
  for (const item of items) {
    const group = item.subtitle || "Quick navigation";
    if (!buckets.has(group)) {
      buckets.set(group, []);
      order.push(group);
    }
    buckets.get(group)?.push(item);
  }
  return order.map((group) => ({ group, items: buckets.get(group) ?? [] }));
}

export interface UniversalSearchDialogProps extends Pick<
  UniversalSearchProps,
  | "navItems"
  | "searchPeople"
  | "onNavigate"
  | "onAnalytics"
  | "iconMap"
  | "placeholder"
  | "peopleSectionLabel"
> {
  isOpen: boolean;
  onClose: () => void;
}

export function UniversalSearchDialog({
  isOpen,
  onClose,
  navItems,
  searchPeople,
  onNavigate,
  onAnalytics,
  iconMap,
  placeholder = "Search…",
  peopleSectionLabel = "People",
}: UniversalSearchDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [personResults, setPersonResults] = useState<PersonResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Search people on debounced query change
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setPersonResults([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    searchPeople(debouncedQuery).then((results) => {
      if (!cancelled) {
        setPersonResults(results);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, searchPeople]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setSelectedIndex(0);
  };

  const navResultItems: NavResult[] = filterNavItems(navItems, query).map((item) => ({
    kind: "nav",
    id: `${item.label}::${item.pathname}`,
    label: item.label,
    subtitle: item.group,
    pathname: item.pathname,
    iconKey: item.iconKey,
  }));

  const personResultItems: PersonResultInternal[] = (!isLoading ? personResults : []).map((p) => ({
    kind: "person",
    id: p.id,
    label: p.label,
    subtitle: p.subtitle,
    pathname: p.pathname,
    initials: p.initials,
  }));

  const allResults: ResultItem[] = [...personResultItems, ...navResultItems];

  const allResultsRef = useRef(allResults);
  allResultsRef.current = allResults;
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  const handleSelect = useCallback(
    (item: ResultItem, index: number) => {
      onAnalytics?.("universal_search:result_click", {
        result_type: item.kind,
        result_label: item.label,
        query,
        result_index: index,
      });
      onClose();
      onNavigate(item.pathname);
    },
    [onAnalytics, onClose, onNavigate, query],
  );

  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        // The only native tab stop inside the dialog is the input — every
        // result row is tabIndex={-1} and navigated via aria-activedescendant
        // instead. Without this, Tab/Shift+Tab falls through to whatever the
        // browser's default tab order is next, moving focus behind the modal
        // overlay (violates the ARIA APG modal dialog pattern).
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      const results = allResultsRef.current;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const idx = selectedIndexRef.current;
        const item = results[idx];
        if (item) handleSelectRef.current(item, idx);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  // Reset + focus on open
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setPersonResults([]);
      setSelectedIndex(0);
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    ) as HTMLElement | null;
    el?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  if (!isOpen) return null;

  const personOffset = 0;
  const navOffset = personResultItems.length;

  const showEmpty = !isLoading && debouncedQuery.length >= 2 && allResults.length === 0;

  // Flat keyboard-nav index of each nav item, independent of how the browsing
  // view below buckets them into groups — Arrow/Enter handling always walks
  // `allResults` in this same order.
  const navIndexById = new Map(navResultItems.map((item, i) => [item.id, navOffset + i]));
  const navGroups = query ? null : groupByCategory(navResultItems);

  return createPortal(
    <div className="fn-us-overlay" onClick={onClose} aria-hidden={!isOpen}>
      <div
        role="dialog"
        aria-label="Universal search"
        aria-modal="true"
        className="fn-us-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Search input ─────────────────────────────────────── */}
        <div className="fn-us-input-row">
          <SearchSvg className="fn-us-search-icon" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={allResults.length > 0}
            aria-controls="fn-us-listbox"
            aria-autocomplete="list"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={placeholder}
            aria-label="Universal search"
            className="fn-us-input"
          />
          {isLoading ? (
            <div className="fn-us-spinner" aria-hidden="true" />
          ) : (
            <div className="fn-us-kbd-group">
              <Kbd>⌘</Kbd>
              <Kbd>K</Kbd>
            </div>
          )}
        </div>

        {/* ── Results ─────────────────────────────────────────── */}
        {allResults.length > 0 && (
          <div
            ref={listRef}
            id="fn-us-listbox"
            role="listbox"
            aria-label="Search results"
            aria-activedescendant={
              allResults.length > 0 ? `fn-us-result-${selectedIndex}` : undefined
            }
            className="fn-us-results"
          >
            {personResultItems.length > 0 && (
              <div role="group" aria-label={peopleSectionLabel}>
                <SectionHeader label={peopleSectionLabel} />
                {personResultItems.map((item, i) => (
                  <ResultRow
                    key={item.id}
                    item={item}
                    index={personOffset + i}
                    isSelected={selectedIndex === personOffset + i}
                    onSelect={() => handleSelect(item, personOffset + i)}
                    onHover={() => setSelectedIndex(personOffset + i)}
                    iconMap={iconMap}
                  />
                ))}
              </div>
            )}
            {navResultItems.length > 0 && navGroups ? (
              // Browsing (no query): group by category (Core/Products/
              // Settings/…) so the palette mirrors the consumer's own
              // sidebar sections instead of one flat list.
              navGroups.map(({ group, items }) => (
                <div key={group} role="group" aria-label={group}>
                  <SectionHeader label={group} />
                  {items.map((item) => {
                    const index = navIndexById.get(item.id) ?? navOffset;
                    return (
                      <ResultRow
                        key={item.id}
                        item={item}
                        index={index}
                        isSelected={selectedIndex === index}
                        onSelect={() => handleSelect(item, index)}
                        onHover={() => setSelectedIndex(index)}
                        iconMap={iconMap}
                      />
                    );
                  })}
                </div>
              ))
            ) : navResultItems.length > 0 ? (
              // Searching: a single ranked list, not regrouped by category.
              <div role="group" aria-label="Pages">
                <SectionHeader label="Pages" />
                {navResultItems.map((item, i) => (
                  <ResultRow
                    key={item.id}
                    item={item}
                    index={navOffset + i}
                    isSelected={selectedIndex === navOffset + i}
                    onSelect={() => handleSelect(item, navOffset + i)}
                    onHover={() => setSelectedIndex(navOffset + i)}
                    iconMap={iconMap}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* ── Empty state ──────────────────────────────────────── */}
        {showEmpty && (
          <div className="fn-us-empty">
            <SearchSvg className="fn-us-empty-icon" />
            <p className="fn-us-empty-title">No results for &ldquo;{debouncedQuery}&rdquo;</p>
            <p className="fn-us-empty-subtitle">Try a name, email, or page name.</p>
          </div>
        )}

        {/* ── Footer keyboard hints ────────────────────────────── */}
        <div className="fn-us-footer">
          <HintGroup keys={["↑", "↓"]} label="navigate" />
          <HintGroup keys={["↵"]} label="open" />
          <HintGroup keys={["Esc"]} label="close" />
        </div>
      </div>
    </div>,
    document.body,
  );
}
